import React from 'react';
import { useAuthStore } from '../stores/authStore';
import type { User } from '../stores/authStore';
import { logger } from '../utils/logger';

/**
 * Role-based access control hook
 * 
 * Provides utilities for checking user roles and permissions
 * for admin features in the communication system.
 */

export type UserRole = 'admin' | 'organizer' | 'member' | 'finance_manager';

export interface RolePermissions {
  // Configuration management permissions
  canViewConfigurations: boolean;
  canEditConfigurations: boolean;
  canDeleteConfigurations: boolean;
  
  // User preference management permissions
  canViewAllPreferences: boolean;
  canEditAllPreferences: boolean;
  canDeleteAllPreferences: boolean;
  
  // Sender configuration permissions
  canViewSenderConfigs: boolean;
  canEditSenderConfigs: boolean;
  canDeleteSenderConfigs: boolean;
  canTestSenderConfigs: boolean;
  
  // Retry policy permissions
  canViewRetryPolicies: boolean;
  canEditRetryPolicies: boolean;
  canDeleteRetryPolicies: boolean;
  
  // Communication permissions
  canViewAllCommunications: boolean;
  canSendCommunications: boolean;
  canScheduleCommunications: boolean;
  canDeleteCommunications: boolean;
  canResendCommunications: boolean;
  
  // Template permissions
  canViewTemplates: boolean;
  canEditTemplates: boolean;
  canDeleteTemplates: boolean;
  canPublishTemplates: boolean;
  
  // Analytics and reporting permissions
  canViewAnalytics: boolean;
  canExportData: boolean;
  canViewSystemHealth: boolean;
  
  // User management permissions
  canViewAllUsers: boolean;
  canEditUsers: boolean;
  canDeleteUsers: boolean;
  canManageRoles: boolean;
  
  // Payment and dues management permissions
  canManagePayments: boolean;
  canRecordManualPayments: boolean;
  canInitiateRefunds: boolean;
  canViewAllPayments: boolean;
  canManageDues: boolean;
  canViewFinancialReports: boolean;

  // Document management permissions
  canViewDocuments: boolean;
  canUploadDocuments: boolean;
  canManageDocuments: boolean;
  canDeleteDocuments: boolean;
  canManageFolders: boolean;

  // Family tree admin management
  canManageTreeAdmins: boolean;

  // Admin dashboard access
  canViewAdmin: boolean;
}

/**
 * Role-based permission matrix
 * Defines what each role can do in the system
 */
const ROLE_PERMISSIONS: Record<UserRole, RolePermissions> = {
  admin: {
    // Configuration management - Full access
    canViewConfigurations: true,
    canEditConfigurations: true,
    canDeleteConfigurations: true,
    
    // User preferences - Full access
    canViewAllPreferences: true,
    canEditAllPreferences: true,
    canDeleteAllPreferences: true,
    
    // Sender configurations - Full access
    canViewSenderConfigs: true,
    canEditSenderConfigs: true,
    canDeleteSenderConfigs: true,
    canTestSenderConfigs: true,
    
    // Retry policies - Full access
    canViewRetryPolicies: true,
    canEditRetryPolicies: true,
    canDeleteRetryPolicies: true,
    
    // Communications - Full access
    canViewAllCommunications: true,
    canSendCommunications: true,
    canScheduleCommunications: true,
    canDeleteCommunications: true,
    canResendCommunications: true,
    
    // Templates - Full access
    canViewTemplates: true,
    canEditTemplates: true,
    canDeleteTemplates: true,
    canPublishTemplates: true,
    
    // Analytics - Full access
    canViewAnalytics: true,
    canExportData: true,
    canViewSystemHealth: true,
    
    // User management - Full access
    canViewAllUsers: true,
    canEditUsers: true,
    canDeleteUsers: true,
    canManageRoles: true,
    
    // Payment and dues management - Full access
    canManagePayments: true,
    canRecordManualPayments: true,
    canInitiateRefunds: true,
    canViewAllPayments: true,
    canManageDues: true,
    canViewFinancialReports: true,

    // Document management - Full access
    canViewDocuments: true,
    canUploadDocuments: true,
    canManageDocuments: true,
    canDeleteDocuments: true,
    canManageFolders: true,

    // Family tree admin management - Full access
    canManageTreeAdmins: true,

    // Admin dashboard access - Full access
    canViewAdmin: true,
  },

  organizer: {
    // Configuration management - View only
    canViewConfigurations: true,
    canEditConfigurations: false,
    canDeleteConfigurations: false,
    
    // User preferences - Limited access
    canViewAllPreferences: true,
    canEditAllPreferences: false,
    canDeleteAllPreferences: false,
    
    // Sender configurations - View and test only
    canViewSenderConfigs: true,
    canEditSenderConfigs: false,
    canDeleteSenderConfigs: false,
    canTestSenderConfigs: true,
    
    // Retry policies - View only
    canViewRetryPolicies: true,
    canEditRetryPolicies: false,
    canDeleteRetryPolicies: false,
    
    // Communications - Most access except delete
    canViewAllCommunications: true,
    canSendCommunications: true,
    canScheduleCommunications: true,
    canDeleteCommunications: false,
    canResendCommunications: true,
    
    // Templates - Create and edit, but not delete
    canViewTemplates: true,
    canEditTemplates: true,
    canDeleteTemplates: false,
    canPublishTemplates: false,
    
    // Analytics - View only
    canViewAnalytics: true,
    canExportData: true,
    canViewSystemHealth: false,
    
    // User management - View only
    canViewAllUsers: true,
    canEditUsers: false,
    canDeleteUsers: false,
    canManageRoles: false,
    
    // Payment and dues management - No access
    canManagePayments: false,
    canRecordManualPayments: false,
    canInitiateRefunds: false,
    canViewAllPayments: false,
    canManageDues: false,
    canViewFinancialReports: false,

    // Document management - Upload and manage, no delete
    canViewDocuments: true,
    canUploadDocuments: true,
    canManageDocuments: true,
    canDeleteDocuments: false,
    canManageFolders: true,

    // Family tree admin management - No access
    canManageTreeAdmins: false,

    // Admin dashboard access - Limited access (organizers can view some admin features)
    canViewAdmin: true,
  },

  member: {
    // Configuration management - No access
    canViewConfigurations: false,
    canEditConfigurations: false,
    canDeleteConfigurations: false,
    
    // User preferences - Own preferences only (handled in components)
    canViewAllPreferences: false,
    canEditAllPreferences: false,
    canDeleteAllPreferences: false,
    
    // Sender configurations - No access
    canViewSenderConfigs: false,
    canEditSenderConfigs: false,
    canDeleteSenderConfigs: false,
    canTestSenderConfigs: false,
    
    // Retry policies - No access
    canViewRetryPolicies: false,
    canEditRetryPolicies: false,
    canDeleteRetryPolicies: false,
    
    // Communications - No access to admin features
    canViewAllCommunications: false,
    canSendCommunications: false,
    canScheduleCommunications: false,
    canDeleteCommunications: false,
    canResendCommunications: false,
    
    // Templates - View only
    canViewTemplates: true,
    canEditTemplates: false,
    canDeleteTemplates: false,
    canPublishTemplates: false,
    
    // Analytics - No access
    canViewAnalytics: false,
    canExportData: false,
    canViewSystemHealth: false,
    
    // User management - No access
    canViewAllUsers: false,
    canEditUsers: false,
    canDeleteUsers: false,
    canManageRoles: false,
    
    // Payment and dues management - No access
    canManagePayments: false,
    canRecordManualPayments: false,
    canInitiateRefunds: false,
    canViewAllPayments: false,
    canManageDues: false,
    canViewFinancialReports: false,

    // Document management - View only
    canViewDocuments: true,
    canUploadDocuments: false,
    canManageDocuments: false,
    canDeleteDocuments: false,
    canManageFolders: false,

    // Family tree admin management - No access
    canManageTreeAdmins: false,

    // Admin dashboard access - No access
    canViewAdmin: false,
  },

  finance_manager: {
    // Configuration management - View only (limited access for finance role)
    canViewConfigurations: true,
    canEditConfigurations: false,
    canDeleteConfigurations: false,
    
    // User preferences - View access (for financial reporting)
    canViewAllPreferences: true,
    canEditAllPreferences: false,
    canDeleteAllPreferences: false,
    
    // Sender configurations - No access (not relevant for finance)
    canViewSenderConfigs: false,
    canEditSenderConfigs: false,
    canDeleteSenderConfigs: false,
    canTestSenderConfigs: false,
    
    // Retry policies - No access
    canViewRetryPolicies: false,
    canEditRetryPolicies: false,
    canDeleteRetryPolicies: false,
    
    // Communications - View access for financial communications
    canViewAllCommunications: true,
    canSendCommunications: true,
    canScheduleCommunications: false,
    canDeleteCommunications: false,
    canResendCommunications: false,
    
    // Templates - View and edit financial templates only
    canViewTemplates: true,
    canEditTemplates: true,
    canDeleteTemplates: false,
    canPublishTemplates: false,
    
    // Analytics - Full access for financial reporting
    canViewAnalytics: true,
    canExportData: true,
    canViewSystemHealth: false,
    
    // User management - View access for financial reporting
    canViewAllUsers: true,
    canEditUsers: false,
    canDeleteUsers: false,
    canManageRoles: false,
    
    // Payment and dues management - Full access
    canManagePayments: true,
    canRecordManualPayments: true,
    canInitiateRefunds: true,
    canViewAllPayments: true,
    canManageDues: true,
    canViewFinancialReports: true,

    // Document management - View only (for financial documents)
    canViewDocuments: true,
    canUploadDocuments: false,
    canManageDocuments: false,
    canDeleteDocuments: false,
    canManageFolders: false,

    // Family tree admin management - No access
    canManageTreeAdmins: false,

    // Admin panel access — Finance Manager needs this to reach /admin/payments/reports
    // and other finance routes. The sidebar already limits visible sections to finance-only items.
    canViewAdmin: true,
  },
};

export interface UseRoleAccessReturn {
  user: User | null;
  role: UserRole | null;
  permissions: RolePermissions;
  
  // Role checking utilities
  isAdmin: boolean;
  isOrganizer: boolean;
  isMember: boolean;
  isFinanceManager: boolean;
  isAdminOrOrganizer: boolean;
  
  // Permission checking utilities
  hasPermission: (permission: keyof RolePermissions) => boolean;
  hasAnyPermission: (permissions: Array<keyof RolePermissions>) => boolean;
  hasAllPermissions: (permissions: Array<keyof RolePermissions>) => boolean;
  
  // Route access checking
  canAccessAdminRoutes: boolean;
  canAccessConfigurationRoutes: boolean;
  canAccessAnalyticsRoutes: boolean;
  canAccessUserManagementRoutes: boolean;
}

/**
 * Hook for role-based access control
 */
export function useRoleAccess(): UseRoleAccessReturn {
  const { user } = useAuthStore();
  
  // Convert role to lowercase to handle backend/frontend case mismatch
  const rawRole = user?.role as string;
  const role = rawRole ? rawRole.toLowerCase() as UserRole : null;
  
  logger.debug('useRoleAccess - role:', { rawRole, convertedRole: role, isAuthenticated: !!user });

  // Ensure we always have valid permissions
  let permissions: RolePermissions;
  if (!role || !user) {
    logger.debug('useRoleAccess - no role/user, using member permissions');
    permissions = { ...ROLE_PERMISSIONS.member };
  } else if (role in ROLE_PERMISSIONS) {
    permissions = { ...ROLE_PERMISSIONS[role] };
    logger.debug('useRoleAccess - permissions loaded:', { role });
  } else {
    logger.warn(`useRoleAccess - unknown role "${rawRole}", falling back to member permissions`);
    permissions = { ...ROLE_PERMISSIONS.member };
  }
  
  const isAdmin = role === 'admin';
  const isOrganizer = role === 'organizer';
  const isMember = role === 'member';
  const isFinanceManager = role === 'finance_manager';
  const isAdminOrOrganizer = isAdmin || isOrganizer;
  
  const hasPermission = (permission: keyof RolePermissions): boolean => {
    return permissions[permission];
  };
  
  const hasAnyPermission = (permissionList: Array<keyof RolePermissions>): boolean => {
    return permissionList.some(permission => permissions[permission]);
  };
  
  const hasAllPermissions = (permissionList: Array<keyof RolePermissions>): boolean => {
    return permissionList.every(permission => permissions[permission]);
  };
  
  // Route access checking
  const canAccessAdminRoutes = isAdminOrOrganizer;
  const canAccessConfigurationRoutes = hasPermission('canViewConfigurations');
  const canAccessAnalyticsRoutes = hasPermission('canViewAnalytics');
  const canAccessUserManagementRoutes = hasPermission('canViewAllUsers');
  
  return {
    user,
    role,
    permissions,
    
    isAdmin,
    isOrganizer,
    isMember,
    isFinanceManager,
    isAdminOrOrganizer,
    
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    
    canAccessAdminRoutes,
    canAccessConfigurationRoutes,
    canAccessAnalyticsRoutes,
    canAccessUserManagementRoutes,
  };
}

/**
 * HOC for role-based component access control
 */
export function withRoleAccess<T extends object>(
  Component: React.ComponentType<T>,
  requiredPermissions: Array<keyof RolePermissions>,
  fallbackComponent?: React.ComponentType<T>
) {
  return function ProtectedComponent(props: T) {
    const { hasAllPermissions } = useRoleAccess();
    
    if (!hasAllPermissions(requiredPermissions)) {
      if (fallbackComponent) {
        const FallbackComponent = fallbackComponent;
        return <FallbackComponent {...props} />;
      }
      
      return (
        <div className="p-6 text-center">
          <div className="max-w-md mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-red-800 mb-2">
                Access Denied
              </h3>
              <p className="text-red-600">
                You don't have the required permissions to access this feature.
              </p>
              <p className="text-red-500 text-sm mt-2">
                Required permissions: {requiredPermissions.join(', ')}
              </p>
            </div>
          </div>
        </div>
      );
    }
    
    return <Component {...props} />;
  };
}

/**
 * Role-based route protection component
 */
export interface RoleProtectedRouteProps {
  children: React.ReactNode;
  requiredPermissions: Array<keyof RolePermissions>;
  fallback?: React.ReactNode;
}

export function RoleProtectedRoute({ 
  children, 
  requiredPermissions, 
  fallback 
}: RoleProtectedRouteProps) {
  const { hasAllPermissions, permissions, role } = useRoleAccess();
  
  const hasAccess = hasAllPermissions(requiredPermissions);
  
  logger.debug('RoleProtectedRoute - access check:', { requiredPermissions, userRole: role, hasAccess });

  if (!hasAccess) {
    logger.warn('RoleProtectedRoute - access denied:', {
      role,
      required: requiredPermissions,
      missing: requiredPermissions.filter(perm => !permissions[perm]),
    });
    
    if (fallback) {
      return <>{fallback}</>;
    }
    
    return (
      <div className="min-h-screen bg-surface-0 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-white border border-border rounded-lg shadow-sm p-8">
            <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-text-primary mb-3">
              Access Denied
            </h3>
            <p className="text-text-secondary mb-4">
              You don't have the required permissions to access this page.
            </p>
            <p className="text-sm text-text-tertiary">
              Contact your administrator if you believe you should have access.
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  logger.debug('RoleProtectedRoute - access granted:', { role, required: requiredPermissions });

  return <>{children}</>;
}